import React, { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Snackbar, CircularProgress, Backdrop } from "@mui/material";
import logo from "../../assets/img/pupill_max512.png";
import mascot from "../../assets/img/mascot.png";
import backgroundImage from "../../assets/img/background.jpg";
import {
  useForgotPasswordMutation,
  useLoginMutation,
  useValidateTemplateCodeMutation,
  useSendRegistrationCodeMutation,
  useVerifyAndRegisterMutation,
} from "../../store/authApi";
import useAuth from "../../hooks/useAuth";
import { formatTRPhone, formatPhoneDisplay } from "../../components/atoms/PhoneInput";

const Login = () => {
  const [authView, setAuthView] = useState("login"); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [regPuserNo, setRegPuserNo] = useState("");
  const [regTemplateCode, setRegTemplateCode] = useState("");
  const [regTemplateName, setRegTemplateName] = useState("");
  const [regName, setRegName] = useState("");
  const [regSurname, setRegSurname] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regTellNo, setRegTellNo] = useState("");
  const [regTcNo, setRegTcNo] = useState("");
  const [regGender, setRegGender] = useState("Erkek");
  const [regStep, setRegStep] = useState(1); // 1: template code, 2: personal info, 3: verify code
  const [regVerificationCode, setRegVerificationCode] = useState("");
  const [regClinicName, setRegClinicName] = useState("");

  const submittingRef = useRef(false); // çift gönderimi engeller

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [showRedirectSpinner, setShowRedirectSpinner] = useState(false);
  const navigate = useNavigate();
  const { setAuth } = useAuth();

  const [login, { isLoading }] = useLoginMutation();
  const [forgotPassword, { isLoading: isForgotLoading }] =
    useForgotPasswordMutation();
  const [validateTemplateCode, { isLoading: isVerifyTemplateLoading }] =
    useValidateTemplateCodeMutation();
  const [sendRegistrationCode, { isLoading: isRegisterLoading }] =
    useSendRegistrationCodeMutation();
  const [verifyAndRegister, { isLoading: isVerifyLoading }] =
    useVerifyAndRegisterMutation();

  const showAlert = (message, severity = "error") => {
    setAlert({ open: true, message, severity });
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Login başarılı olduğunda rol bazlı yönlendirme
  const handleLoginComplete = useCallback(
    (result) => {
      showAlert("Giriş başarılı !", "success");
      const accessToken = result?.token;
      const user = result?.data?.user;

      setAuth({ ...user, accessToken });
      setShowRedirectSpinner(true);

      const roleRoutes = {
        Admin: "/Admin-Dashboard",
        ClinicStaff: "/Genel-Bakis",
      };
      const route = roleRoutes[user?.role];
      if (route) {
        setTimeout(() => navigate(route), 2000);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, setAuth]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const result = await login({ email, password }).unwrap();
      handleLoginComplete(result);
    } catch (err) {
      const errorMessage =
        err?.data?.message || err?.message || "İşlem başarısız tekrar deneyin !";
      if (err?.status === 403) {
        showAlert(errorMessage, "warning");
      } else {
        showAlert(errorMessage, "error");
      }
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await forgotPassword({ email: forgotEmail }).unwrap();
      setForgotSent(true);
    } catch (err) {
      console.error("Yeni şifre gönderme hatası:", err);
      showAlert(
        err?.data?.message || "Yeni şifre gönderilirken bir hata oluştu",
        "error"
      );
    } finally {
      submittingRef.current = false;
    }
  };

  const resetLoginState = () => {
    setEmail("");
    setPassword("");
    setShowPassword(false);
  };

  const resetForgotState = () => {
    setForgotEmail("");
    setForgotSent(false);
  };

  const resetRegisterState = () => {
    setRegPuserNo("");
    setRegTemplateCode("");
    setRegTemplateName("");
    setRegName("");
    setRegSurname("");
    setRegEmail("");
    setRegPassword("");
    setRegTellNo("");
    setRegTcNo("");
    setRegGender("Erkek");
    setRegStep(1);
    setRegVerificationCode("");
    setRegClinicName("");
  };

  const handleVerifyTemplateCode = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      const res = await validateTemplateCode({
        puserNo: Number(regPuserNo),
        templateCode: regTemplateCode,
      }).unwrap();
      setRegClinicName(res?.data?.clinicName || "");
      setRegTemplateName(res?.data?.templateName || "");
      setRegStep(2);
      showAlert("Puser numarası ve şablon kodu doğrulandı.", "success");
    } catch (err) {
      showAlert(
        err?.data?.message || "Puser numarası veya şablon kodu doğrulanamadı, tekrar deneyin.",
        "error"
      );
    } finally {
      submittingRef.current = false;
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await sendRegistrationCode({
        puserNo: Number(regPuserNo),
        templateCode: regTemplateCode,
        name: regName,
        surname: regSurname,
        email: regEmail,
        password: regPassword,
        tellNo: regTellNo,
        tcNo: regTcNo,
        gender: regGender,
      }).unwrap();
      setRegStep(3);
      showAlert("Doğrulama kodu e-postanıza gönderildi.", "success");
    } catch (err) {
      showAlert(
        err?.data?.message || "Kayıt işlemi başlatılamadı, tekrar deneyin.",
        "error"
      );
    } finally {
      submittingRef.current = false;
    }
  };

  const handleVerifyRegister = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await verifyAndRegister({
        puserNo: Number(regPuserNo),
        templateCode: regTemplateCode,
        name: regName,
        surname: regSurname,
        email: regEmail,
        password: regPassword,
        tellNo: regTellNo,
        tcNo: regTcNo,
        gender: regGender,
        verificationCode: regVerificationCode,
      }).unwrap();
      showAlert(
        "Kayıt başvurunuz alındı. Hesabınız klinik yöneticisi onayladığında aktifleşecek.",
        "success"
      );
      setAuthView("login");
      resetRegisterState();
    } catch (err) {
      showAlert(
        err?.data?.message || "Doğrulama başarısız, tekrar deneyin.",
        "error"
      );
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 overflow-hidden">
      {/* Transparent background artwork */}
      <div
        className="pointer-events-none absolute inset-0 mix-blend-soft-light bg-repeat bg-center bg-[length:520px_520px] filter animate-bg-pulse motion-reduce:animate-none"
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      {/* extra vignette for readability */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-950/40 via-transparent to-blue-900/50" />

      <div className="relative w-full max-w-6xl overflow-hidden rounded-[36px] shadow-2xl">
        {/* Subtle background blobs */}
        <div className="pointer-events-none absolute -left-40 -top-40 h-96 w-96 rounded-full bg-white/10 blur-2xl" />
        <div className="pointer-events-none absolute -right-40 -bottom-40 h-96 w-96 rounded-full bg-white/10 blur-2xl" />

        <div className="flex flex-col md:flex-row">
          {/* Left: Welcome */}
          <div
            className={`relative min-w-0 text-white bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 overflow-hidden transition-all duration-500 ease-in-out md:rounded-none ${
              authView === "register"
                ? "md:w-0 md:flex-[0_0_0%] md:max-w-0 md:p-0 md:opacity-0 md:-translate-x-2 md:pointer-events-none"
                : "md:w-1/2 md:flex-[0_0_50%] md:max-w-[50%] md:p-10 md:opacity-100 md:translate-x-0"
            } p-8`}
          >
            {/* decorative blobs */}
            <div className="pointer-events-none absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-white/10 blur-sm" />
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-sm" />
            {/* animated shine overlay */}
            <div className="pointer-events-none absolute inset-0 opacity-60 bg-[linear-gradient(90deg,rgba(255,255,255,0.12),rgba(255,255,255,0.28),rgba(255,255,255,0.12))] bg-[length:200%_200%] animate-hero-shine motion-reduce:animate-none" />

            <div className="relative">
              <img
                src={logo}
                alt="Pupill Logo"
                className="w-56 md:w-72 h-auto object-contain mb-2 drop-shadow-[0_12px_30px_rgba(0,0,0,0.25)]"
              />

              <h1 className="text-4xl md:text-5xl font-extrabold tracking-widest animate-hero-in [animation-delay:.05s] motion-reduce:animate-none">
                HOŞ GELDİNİZ
              </h1>
              <p className="mt-4 text-base tracking-[0.25em] font-semibold text-blue-100 animate-hero-in [animation-delay:.18s] motion-reduce:animate-none">
                PUPILL PANEL
              </p>
              <div className="mt-3 h-1 w-16 rounded-full bg-white/30 animate-hero-in [animation-delay:.18s] motion-reduce:animate-none" />
              <p className="mt-5 max-w-md pr-28 md:pr-40 lg:pr-48 text-sm md:text-base leading-7 text-blue-50/90 animate-hero-in [animation-delay:.3s] motion-reduce:animate-none">
                Güvenli giriş ile cihazlarınızı, hastalarınızı ve klinik
                süreçlerinizi tek panelden yönetin.
              </p>
            </div>

            {/* Mascot — panelin sağ alt köşesi */}
            <img
              src={mascot}
              alt="Pupill Maskotu"
              className={`pointer-events-none absolute bottom-0 right-0 w-32 md:w-40 lg:w-48 drop-shadow-[0_18px_40px_rgba(0,0,0,0.35)] animate-hero-float motion-reduce:animate-none ${
                authView === "register" ? "md:hidden" : ""
              }`}
            />
          </div>

          {/* Right: Sign in */}
          <div
            className={`min-w-0 bg-white/95 backdrop-blur-md p-8 md:p-12 flex items-center transition-all duration-500 ease-in-out ${
              authView === "register"
                ? "md:w-full md:flex-[1_1_100%] md:max-w-full"
                : "md:w-1/2 md:flex-[0_0_50%] md:max-w-[50%]"
            }`}
          >
            <div
              className={`w-full mx-auto ${
                authView === "register" ? "max-w-4xl" : "max-w-md"
              }`}
            >
              {authView === "login" ? (
                <>
                  <h2 className="text-4xl font-extrabold text-slate-800">
                    Giriş
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Hesabınıza giriş yapın.
                  </p>

                  <form
                    className="mt-8 flex flex-col gap-5"
                    onSubmit={handleSubmit}
                  >
                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        E-posta
                      </label>
                      <input
                        type="email"
                        className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="ornek@mail.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={isLoading}
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        Şifre
                      </label>
                      <div className="mt-2 relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-20 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Şifre"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-2 my-auto h-9 px-3 rounded-lg text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                          onClick={() => setShowPassword((s) => !s)}
                          disabled={isLoading}
                        >
                          {showPassword ? "GİZLE" : "GÖSTER"}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-slate-600 select-none">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          disabled={isLoading}
                        />
                        Beni hatırla
                      </label>

                      <button
                        type="button"
                        className="text-sm font-semibold text-blue-700 hover:underline disabled:opacity-50"
                        onClick={() => {
                          resetLoginState();
                          resetForgotState();
                          setAuthView("forgot");
                        }}
                        disabled={isLoading}
                      >
                        Şifremi unuttum
                      </button>
                    </div>

                    <button
                      type="submit"
                      className="mt-2 w-full rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <CircularProgress size={20} color="inherit" />
                          Giriş Yapılıyor...
                        </>
                      ) : (
                        "Giriş Yap"
                      )}
                    </button>

                    <p className="text-center text-sm text-slate-500">
                      Hesabınız yok mu?{" "}
                      <button
                        type="button"
                        className="font-semibold text-blue-700 hover:underline"
                        onClick={() => {
                          setAuthView("register");
                          resetLoginState();
                          resetForgotState();
                          resetRegisterState();
                        }}
                        disabled={isLoading}
                      >
                        Kayıt Ol
                      </button>
                    </p>
                  </form>
                </>
              ) : authView === "forgot" ? (
                <>
                  <h2 className="text-3xl font-extrabold text-slate-800">
                    Şifre Sıfırlama
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    E-posta adresinizi yazın, sıfırlama işlemini başlatalım.
                  </p>

                  <form
                    onSubmit={handleForgotSubmit}
                    className="mt-8 flex flex-col gap-5"
                  >
                    {forgotSent ? (
                      <Alert severity="success">
                        Yeni şifreniz e-posta adresinize gönderildi!
                      </Alert>
                    ) : null}

                    <div>
                      <label className="text-sm font-medium text-slate-700">
                        E-posta
                      </label>
                      <input
                        type="email"
                        className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="ornek@mail.com"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        required
                        disabled={isForgotLoading}
                      />
                    </div>

                    <button
                      type="submit"
                      className="mt-2 w-full rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-semibold py-3 shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      disabled={isForgotLoading}
                    >
                      {isForgotLoading ? (
                        <>
                          <CircularProgress size={20} color="inherit" />
                          Gönderiliyor...
                        </>
                      ) : (
                        "Yeni Şifre Gönder"
                      )}
                    </button>

                    <p className="text-center text-sm text-slate-500">
                      Giriş ekranına dönmek ister misiniz?{" "}
                      <button
                        type="button"
                        className="font-semibold text-blue-700 hover:underline"
                        onClick={() => {
                          resetForgotState();
                          resetLoginState();
                          setAuthView("login");
                        }}
                        disabled={isForgotLoading}
                      >
                        Giriş
                      </button>
                    </p>
                  </form>
                </>
              ) : (
                <>
                  <h2 className="text-3xl font-extrabold text-slate-800">
                    Şablon Kodu ile Kayıt
                  </h2>
                  <p className="mt-2 text-sm text-slate-500">
                    Kayıt talebiniz önce <b>Beklemede</b> olur, kliniğiniz
                    onaylarsa aktifleşir.
                  </p>

                  {/* Steps */}
                  <div className="mt-6 flex items-center gap-3">
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        regStep === 1
                          ? "bg-blue-700 text-white"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      1
                    </div>
                    <div className="h-[2px] flex-1 bg-slate-200" />
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        regStep === 2
                          ? "bg-blue-700 text-white"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      2
                    </div>
                    <div className="h-[2px] flex-1 bg-slate-200" />
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        regStep === 3
                          ? "bg-blue-700 text-white"
                          : "bg-blue-100 text-blue-800"
                      }`}
                    >
                      3
                    </div>
                  </div>

                  {regStep === 1 ? (
                    <form
                      onSubmit={handleVerifyTemplateCode}
                      className="mt-6 flex flex-col gap-4"
                    >
                      <div>
                        <label className="text-sm font-medium text-slate-700">
                          Puser Numarası
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 tracking-widest text-center"
                          placeholder="Örn: 1042"
                          value={regPuserNo}
                          onChange={(e) =>
                            setRegPuserNo(e.target.value.replace(/[^0-9]/g, ""))
                          }
                          required
                          disabled={isVerifyTemplateLoading}
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium text-slate-700">
                          Şablon Kodu
                        </label>
                        <input
                          type="text"
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 uppercase tracking-widest text-center"
                          placeholder="Örn: ABC123"
                          maxLength={6}
                          value={regTemplateCode}
                          onChange={(e) =>
                            setRegTemplateCode(e.target.value.toUpperCase())
                          }
                          required
                          disabled={isVerifyTemplateLoading}
                        />
                      </div>

                      <button
                        type="submit"
                        className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-xl py-3 mt-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        disabled={isVerifyTemplateLoading}
                      >
                        {isVerifyTemplateLoading ? (
                          <>
                            <CircularProgress size={16} color="inherit" />
                            Doğrulanıyor...
                          </>
                        ) : (
                          "Doğrula"
                        )}
                      </button>

                      <p className="text-center text-sm text-slate-500">
                        Zaten hesabınız var mı?{" "}
                        <button
                          type="button"
                          className="font-semibold text-blue-700 hover:underline"
                          onClick={() => {
                            setAuthView("login");
                            resetRegisterState();
                            resetForgotState();
                            resetLoginState();
                          }}
                        >
                          Giriş
                        </button>
                      </p>
                    </form>
                  ) : regStep === 2 ? (
                    <form
                      onSubmit={handleRegisterSubmit}
                      className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                      <div className="md:col-span-2 lg:col-span-3">
                        <p className="text-sm text-slate-600">
                          Klinik: <b>{regClinicName}</b> — Puser:{" "}
                          <b>{regPuserNo}</b> — Rol: <b>{regTemplateName}</b>
                        </p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-slate-700">
                          Ad
                        </label>
                        <input
                          type="text"
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={regName}
                          onChange={(e) => setRegName(e.target.value)}
                          required
                          disabled={isRegisterLoading || isVerifyLoading}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700">
                          Soyad
                        </label>
                        <input
                          type="text"
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={regSurname}
                          onChange={(e) => setRegSurname(e.target.value)}
                          required
                          disabled={isRegisterLoading || isVerifyLoading}
                        />
                      </div>
                      <div className="lg:col-span-1">
                        <label className="text-sm font-medium text-slate-700">
                          Cinsiyet
                        </label>
                        <select
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={regGender}
                          onChange={(e) => setRegGender(e.target.value)}
                          disabled={isRegisterLoading || isVerifyLoading}
                        >
                          <option value="Erkek">Erkek</option>
                          <option value="Kadın">Kadın</option>
                        </select>
                      </div>

                      <div className="md:col-span-2 lg:col-span-3">
                        <label className="text-sm font-medium text-slate-700">
                          E-posta
                        </label>
                        <input
                          type="email"
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={regEmail}
                          onChange={(e) => setRegEmail(e.target.value)}
                          required
                          disabled={isRegisterLoading || isVerifyLoading}
                        />
                      </div>

                      <div className="md:col-span-2 lg:col-span-2">
                        <label className="text-sm font-medium text-slate-700">
                          Şifre
                        </label>
                        <input
                          type="password"
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={regPassword}
                          onChange={(e) => setRegPassword(e.target.value)}
                          required
                          disabled={isRegisterLoading || isVerifyLoading}
                        />
                      </div>

                      <div className="lg:col-span-1">
                        <label className="text-sm font-medium text-slate-700">
                          Telefon
                        </label>
                        <input
                          type="tel"
                          inputMode="tel"
                          maxLength={19}
                          placeholder="+90 (5XX) XXX XX XX"
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={formatPhoneDisplay(regTellNo)}
                          onChange={(e) => setRegTellNo(formatTRPhone(e.target.value))}
                          required
                          disabled={isRegisterLoading || isVerifyLoading}
                        />
                      </div>
                      <div className="lg:col-span-1">
                        <label className="text-sm font-medium text-slate-700">
                          TC No
                        </label>
                        <input
                          type="text"
                          className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          value={regTcNo}
                          onChange={(e) => setRegTcNo(e.target.value)}
                          required
                          disabled={isRegisterLoading || isVerifyLoading}
                        />
                      </div>

                      <div className="md:col-span-2 lg:col-span-3 flex items-center gap-3 mt-2">
                        <button
                          type="button"
                          className="flex-1 rounded-xl border border-slate-200 py-3 font-semibold text-slate-700 hover:bg-slate-50"
                          onClick={() => setRegStep(1)}
                          disabled={isRegisterLoading || isVerifyLoading}
                        >
                          Geri
                        </button>
                        <button
                          type="submit"
                          className="flex-[2] bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-xl py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          disabled={isRegisterLoading || isVerifyLoading}
                        >
                          {isRegisterLoading ? (
                            <>
                              <CircularProgress size={16} color="inherit" />
                              Gönderiliyor...
                            </>
                          ) : (
                            "Doğrulama Kodu Gönder"
                          )}
                        </button>
                      </div>

                      <p className="md:col-span-2 lg:col-span-3 text-center text-sm text-slate-500">
                        Zaten hesabınız var mı?{" "}
                        <button
                          type="button"
                          className="font-semibold text-blue-700 hover:underline"
                          onClick={() => {
                            setAuthView("login");
                            resetRegisterState();
                            resetForgotState();
                            resetLoginState();
                          }}
                        >
                          Giriş
                        </button>
                      </p>
                    </form>
                  ) : (
                    <form
                      onSubmit={handleVerifyRegister}
                      className="mt-6 flex flex-col gap-4"
                    >
                      <label className="text-sm font-medium text-slate-700">
                        E-postanıza gelen doğrulama kodu
                      </label>
                      <input
                        type="text"
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 tracking-[0.35em] text-center uppercase focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="______"
                        value={regVerificationCode}
                        onChange={(e) => setRegVerificationCode(e.target.value)}
                        required
                        disabled={isVerifyLoading || isRegisterLoading}
                      />

                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="flex-1 rounded-xl border border-slate-200 py-3 font-semibold text-slate-700 hover:bg-slate-50"
                          onClick={() => setRegStep(2)}
                          disabled={isVerifyLoading || isRegisterLoading}
                        >
                          Geri
                        </button>
                        <button
                          type="submit"
                          className="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-xl py-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          disabled={isVerifyLoading || isRegisterLoading}
                        >
                          {isVerifyLoading ? (
                            <>
                              <CircularProgress size={16} color="inherit" />
                              Doğrulanıyor...
                            </>
                          ) : (
                            "Email Doğrula"
                          )}
                        </button>
                      </div>

                      <p className="text-center text-sm text-slate-500">
                        Doğrulama sonrası hesabınız <b>Beklemede</b> durumuna
                        geçer ve kliniğiniz onaylayınca aktif olur.
                      </p>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Yönlendirme Spinner Backdrop */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
        }}
        open={showRedirectSpinner}
      >
        <div className="flex flex-col items-center gap-4">
          <CircularProgress color="inherit" size={60} />
        </div>
      </Backdrop>

      {/* MUI Snackbar Alert */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Login;
